/**
 * User-friendly error messages and solutions
 * Maps technical errors to helpful, actionable messages
 */

export interface ErrorMessage {
  title: string;
  description: string;
  action?: string;
}

export const ERROR_MESSAGES: Record<string, ErrorMessage> = {
  // Authentication errors
  'auth/invalid-credentials': {
    title: 'Invalid Credentials',
    description: 'The email or password you entered is incorrect. Please try again.',
    action: 'Double-check your email and password'
  },
  'Invalid login credentials': {
    title: 'Incorrect email or password',
    description: 'Please check your credentials and try again.',
    action: 'Reset password',
  },
  'invalid login credentials': {
    title: 'Incorrect email or password',
    description: 'Please check your credentials and try again.',
    action: 'Reset password',
  },
  'invalid_credentials': {
    title: 'Invalid Credentials',
    description: 'The email or password you entered is incorrect. Please try again.',
    action: 'Double-check your email and password'
  },
  'auth/user-not-found': {
    title: 'Account Not Found',
    description: 'No account exists with this email address.',
    action: 'Sign up for a new account'
  },
  'Email not confirmed': {
    title: 'Please verify your email',
    description: 'Check your inbox for a verification link.',
    action: 'Resend verification email',
  },
  'auth/email-already-in-use': {
    title: 'Email Already Registered',
    description: 'An account with this email already exists.',
    action: 'Try logging in or use a different email'
  },
  'User already registered': {
    title: 'Account already exists',
    description: 'An account with this email already exists.',
    action: 'Try logging in instead',
  },
  'auth/weak-password': {
    title: 'Weak Password',
    description: 'Your password must be at least 8 characters and include numbers and symbols.',
    action: 'Choose a stronger password'
  },
  'Password should be at least 6 characters': {
    title: 'Password too short',
    description: 'Your password must be at least 6 characters long.',
  },
  'auth/invalid-email': {
    title: 'Invalid Email',
    description: 'Please enter a valid email address.',
  },
  'Invalid email': {
    title: 'Invalid email format',
    description: 'Please enter a valid email address.',
  },
  'Email address is invalid': {
    title: 'Invalid Email Address',
    description: 'The email address format is not valid. Please use a valid email address (e.g., name@domain.com).',
  },
  'email address is invalid': {
    title: 'Invalid Email Address',
    description: 'The email address format is not valid. Please use a valid email address (e.g., name@domain.com).',
  },
  'invalid email': {
    title: 'Invalid Email Address',
    description: 'The email address format is not valid. Please use a valid email address.',
  },
  'auth/too-many-requests': {
    title: 'Too Many Attempts',
    description: 'Too many failed login attempts. Please try again later.',
    action: 'Wait a few minutes before trying again'
  },
  'email logins are disabled': {
    title: 'Email Login Disabled',
    description: 'Email/password authentication is currently disabled. Please use Google or Apple sign-in, or contact support.',
    action: 'Use Google or Apple sign-in'
  },
  'Email logins are disabled': {
    title: 'Email Login Disabled',
    description: 'Email/password authentication is currently disabled. Please use Google or Apple sign-in, or contact support.',
    action: 'Use Google or Apple sign-in'
  },
  'auth/email-disabled': {
    title: 'Email Login Disabled',
    description: 'Email/password authentication is currently disabled. Please use Google or Apple sign-in, or contact support.',
    action: 'Use Google or Apple sign-in'
  },

  // RFQ errors
  'rfq/invalid-quantity': {
    title: 'Invalid Quantity',
    description: 'Please enter a valid quantity (minimum 1).',
  },
  'rfq/missing-fields': {
    title: 'Missing Information',
    description: 'Please fill in all required fields before submitting.',
  },
  'rfq/file-too-large': {
    title: 'File Too Large',
    description: 'File size must be under 10MB. Please compress or choose a smaller file.',
  },
  'rfq/invalid-deadline': {
    title: 'Invalid Deadline',
    description: 'Deadline must be in the future.',
  },

  // Quote errors
  'quote/invalid-price': {
    title: 'Invalid Price',
    description: 'Please enter a valid price greater than zero.',
  },
  'quote/expired-rfq': {
    title: 'RFQ Expired',
    description: 'This RFQ has expired and is no longer accepting quotes.',
    action: 'Browse other active RFQs'
  },
  'quote/already-submitted': {
    title: 'Quote Already Submitted',
    description: 'You have already submitted a quote for this RFQ.',
    action: 'Edit your existing quote instead'
  },

  // Order errors
  'order/payment-failed': {
    title: 'Payment Failed',
    description: 'We could not process your payment. Please check your payment method and try again.',
    action: 'Update payment method'
  },
  'order/insufficient-stock': {
    title: 'Insufficient Stock',
    description: 'The supplier does not have enough stock for this order.',
    action: 'Contact the supplier or reduce quantity'
  },

  // Payment errors
  'payment/insufficient-funds': {
    title: 'Payment Declined',
    description: 'Your payment method was declined. Please check your account balance.',
    action: 'Try a different payment method'
  },
  'payment/invalid-card': {
    title: 'Invalid Card',
    description: 'The card details you entered are invalid. Please check and try again.',
  },
  'payment/card-expired': {
    title: 'Card Expired',
    description: 'Your card has expired. Please use a different payment method.',
  },
  'card_declined': {
    title: 'Payment declined',
    description: 'Your card was declined. Please try another payment method.',
    action: 'Update payment method',
  },
  'insufficient_funds': {
    title: 'Insufficient funds',
    description: 'Your card has insufficient funds.',
    action: 'Use different card',
  },

  // Database Errors
  '23505': {
    title: 'Duplicate entry',
    description: 'This item already exists in the system.',
  },
  '23503': {
    title: 'Related item not found',
    description: 'The referenced item no longer exists.',
  },
  '42501': {
    title: 'Access denied',
    description: 'You don\'t have permission to perform this action.',
  },

  // File upload errors
  'upload/file-too-large': {
    title: 'File Too Large',
    description: 'Maximum file size is 10MB. Please choose a smaller file.',
  },
  'upload/invalid-type': {
    title: 'Invalid File Type',
    description: 'This file type is not supported. Please upload a PDF, JPG, PNG, or DOC file.',
  },
  'upload/failed': {
    title: 'Upload Failed',
    description: 'Failed to upload file. Please check your connection and try again.',
  },
  'File too large': {
    title: 'File size limit exceeded',
    description: 'Please upload a file smaller than 10MB.',
  },
  'Invalid file type': {
    title: 'Unsupported file type',
    description: 'Please upload a valid image or PDF file.',
  },

  // Network errors
  'network/timeout': {
    title: 'Request Timeout',
    description: 'The request took too long. Please check your connection and try again.',
  },
  'network/offline': {
    title: 'No Internet Connection',
    description: 'You appear to be offline. Please check your internet connection.',
  },
  'network/server-error': {
    title: 'Server Error',
    description: 'Something went wrong on our end. Please try again in a moment.',
  },
  'Failed to fetch': {
    title: 'Connection problem',
    description: 'Please check your internet connection and try again.',
    action: 'Retry',
  },
  'Network request failed': {
    title: 'Network error',
    description: 'Unable to reach the server. Please try again.',
    action: 'Retry',
  },

  // Permission errors
  'permission/unauthorized': {
    title: 'Unauthorized',
    description: 'You do not have permission to perform this action.',
  },
  'permission/forbidden': {
    title: 'Access Denied',
    description: 'You do not have access to this resource.',
  },

  // Validation errors
  'validation/invalid-input': {
    title: 'Invalid Input',
    description: 'Please check your input and try again.',
  },
  'validation/required-field': {
    title: 'Required Field',
    description: 'This field is required.',
  },

  // Generic fallback
  'unknown': {
    title: 'Something Went Wrong',
    description: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
  },
  'default': {
    title: 'Something went wrong',
    description: 'We\'re having trouble completing your request.',
    action: 'Try again',
  },
};

export function getUserFriendlyError(error: unknown): ErrorMessage {
  // Handle Supabase AuthError objects first (they have message but aren't Error instances)
  if (error && typeof error === 'object' && 'message' in error && !(error instanceof Error)) {
    const errorObj = error as { message?: string; status?: number; name?: string };
    const message = (errorObj.message || '').toLowerCase();
    
    // Check for specific error patterns (more comprehensive matching)
    for (const [key, errorMsg] of Object.entries(ERROR_MESSAGES)) {
      const keyLower = key.toLowerCase();
      if (message.includes(keyLower) || 
          message.includes(keyLower.replace('_', ' ')) ||
          message.includes(keyLower.replace('/', ' '))) {
        return errorMsg;
      }
    }
    
    // Check for Supabase-specific error codes
    if (errorObj.status === 400) {
      if (message.includes('email') && message.includes('invalid')) {
        return ERROR_MESSAGES['Email address is invalid'] || ERROR_MESSAGES['auth/invalid-email'];
      }
      if (message.includes('invalid') || message.includes('credentials')) {
        return ERROR_MESSAGES['Invalid login credentials'] || ERROR_MESSAGES['auth/invalid-credentials'];
      }
    }
    
    // Check for 422 status (email login disabled)
    if (errorObj.status === 422) {
      if (message.includes('email') && (message.includes('disabled') || message.includes('not enabled'))) {
        return ERROR_MESSAGES['email logins are disabled'] || ERROR_MESSAGES['auth/email-disabled'];
      }
      // Generic 422 error for password grant type
      if (message.includes('password') || message.includes('grant_type')) {
        return ERROR_MESSAGES['email logins are disabled'] || {
          title: 'Email Login Disabled',
          description: 'Email/password authentication is currently disabled. Please use Google or Apple sign-in, or contact support.',
          action: 'Use Google or Apple sign-in'
        };
      }
    }
  }
  
  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    
    // Check for specific error patterns
    for (const [key, errorMsg] of Object.entries(ERROR_MESSAGES)) {
      const keyLower = key.toLowerCase();
      if (message.includes(keyLower) || 
          message.includes(keyLower.replace('_', ' ')) ||
          message.includes(keyLower.replace('/', ' '))) {
        return errorMsg;
      }
    }
    
    // Check for common Supabase/Postgres errors
    if (message.includes('unique constraint') || message.includes('duplicate')) {
      return {
        title: 'Duplicate Entry',
        description: 'This item already exists. Please use a different value.',
      };
    }
    
    if (message.includes('foreign key')) {
      return {
        title: 'Invalid Reference',
        description: 'This action references data that no longer exists.',
      };
    }
    
    if (message.includes('not found') || message.includes('404')) {
      return {
        title: 'Not Found',
        description: 'The requested item could not be found.',
      };
    }
    
    if (message.includes('timeout')) {
      return ERROR_MESSAGES['network/timeout'];
    }
    
    return {
      title: 'Error',
      description: error.message,
    };
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return ERROR_MESSAGES[error] || {
      title: 'Error',
      description: error,
    };
  }
  
  // Handle object errors (from API)
  if (error && typeof error === 'object' && 'message' in error) {
    return getUserFriendlyError(new Error(String(error.message)));
  }
  
  return ERROR_MESSAGES.unknown;
}

export function formatValidationErrors(errors: Record<string, string | { message?: string }>): string {
  const messages = Object.entries(errors)
    .map(([field, error]) => {
      const fieldName = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const message = typeof error === 'string' ? error : error?.message || 'Invalid value';
      return `${fieldName}: ${message}`;
    })
    .filter(Boolean);
  
  return messages.length > 0 
    ? messages.join(', ') 
    : 'Please correct the errors and try again.';
}

// Legacy support
export const getFriendlyError = getUserFriendlyError;
